import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import ListLayout from "../../../Components/List";
import FaqFormModal from "./FaqFormModal";
import useListParams from "../../../Helpers/useListParams";

const FAQ_CATEGORIES = ["Car", "Booking", "Payment", "Refund", "Trip Protection", "Support"];
const INITIAL_FILTERS = { search: "", searchKey: "question", count: 10, category: "" };
const SEARCH_KEYS = [{ key: "question", value: "Question / answer / FAQ ID" }];

const FaqList = () => {
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingFaq, setEditingFaq] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const filterItems = {
        dropdown: [
            {
                key: "category",
                name: "Category",
                options: [{ key: "", name: "All" }, ...FAQ_CATEGORIES.map((c) => ({ key: c, name: c }))],
            },
        ],
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/staff/client/faq/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.category ? { category: filters.category } : {}),
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.category]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const openCreate = () => { setEditingFaq(null); setShowModal(true); };
    const openEdit = (faq) => { setEditingFaq(faq); setShowModal(true); };

    const removeFaq = async (faq) => {
        if (!window.confirm(`Delete this FAQ? "${faq.question}"`)) return;
        const response = await Handler({ method: "delete", url: `/staff/client/faq/${faq.faqId}` });
        if (response?.success) {
            toast.success(response?.data?.message || "FAQ deleted");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to delete FAQ"));
        }
    };

    const columns = [
        { key: "faqId", name: "FAQ ID", mandatory: true },
        { key: "category", name: "Category" },
        { key: "question", name: "Question" },
        { key: "answer", name: "Answer", render: (row) => <span className="text-truncate d-inline-block" style={{ maxWidth: 320 }}>{row.answer}</span> },
        {
            key: "actions",
            name: "Actions",
            render: (row) => (
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(row)}>Edit</button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeFaq(row)}>Delete</button>
                </div>
            ),
        },
    ];

    return (
        <>
            <ListLayout
                title="FAQ"
                description="Frequently asked questions shown to customers, organized by category."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={filterItems}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by question, answer, or FAQ ID..."
                addList={openCreate}
            />

            {showModal ? (
                <FaqFormModal
                    faq={editingFaq}
                    onClose={() => setShowModal(false)}
                    onSaved={() => { setShowModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default FaqList;
